import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent,
  List,
  ListItem,
  ListItemContent,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  Modal,
  ModalDialog,
} from '@mui/joy';
import { 
  FaChevronRight,
  FaExternalLinkAlt,
  FaFolder, 
  FaFile, 
  FaDownload, 
  FaUpload,
  FaLink,
  FaUnlink
} from 'react-icons/fa';
import { MdFolderOpen } from 'react-icons/md';
import { IconWrapper } from './IconWrapper';
import { WorkspaceCardHeader } from './WorkspaceCardHeader';
import { 
  listProjectFolderContents, 
  uploadFileToProjectFolder, 
  createOrOpenProjectFolder, 
  linkExistingFolderToProject,
  unlinkFolderFromProject,
  type DriveItem,
  getGraphClientSilent,
  isSignedIn
} from '../utils/graphService';
import type { Project } from '../types/project';
import FolderBrowser from './FolderBrowser';

interface ProjectFolderViewerProps {
  project: Project;
}

const ProjectFolderViewer: React.FC<ProjectFolderViewerProps> = ({ project }) => {
  const [items, setItems] = useState<DriveItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [folderUrl, setFolderUrl] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [folderIdInput, setFolderIdInput] = useState('');
  const [linkingFolder, setLinkingFolder] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [unlinkingFolder, setUnlinkingFolder] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const fileCount = items.filter((item) => !item.folder).length;

  const initializeFolder = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Show waiting message
      setError('Please wait while we create your project folder...');
      
      // Create or open the project folder
      await createOrOpenProjectFolder(project);
      
      // Force a page refresh to ensure clean state
      window.location.reload();
    } catch (err) {
      console.error('Error initializing folder:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize folder');
    } finally {
      setLoading(false);
    }
  };

  const fetchFolderContents = useCallback(async () => {
    if (!initialized) {
      return; // Don't auto-initialize, just return
    }

    try {
      setLoading(true);
      setError(null);
      
      // List folder contents
      const contents = await listProjectFolderContents(project);
      setItems(contents);
    } catch (err) {
      console.error('Error fetching folder contents:', err);
      setError('Failed to load folder contents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [project, initialized]);

  // Only fetch contents if initialized
  useEffect(() => {
    if (initialized) {
      fetchFolderContents();
    }
  }, [fetchFolderContents, initialized]);

  // Add initialization effect
  useEffect(() => {
    const initializeExistingFolder = async () => {
      if (project.onedrive_folder_id && !initialized) {
        try {
          setLoading(true);
          setError(null);
          
          // First check if we're authenticated
          const isAuthenticated = await isSignedIn();
          if (!isAuthenticated) {
            setError('Please sign in to access project folders');
            return;
          }
          
          // Get the folder details
          const client = await getGraphClientSilent();
          if (!client) {
            setError('Please sign in to access project folders');
            return;
          }
          const folder = await client.api(`/me/drive/items/${project.onedrive_folder_id}`)
            .select('id,name,webUrl')
            .get();
          
          if (!folder || !folder.id) {
            throw new Error('Folder not found or access denied');
          }
          
          setFolderUrl(folder.webUrl);
          
          // List folder contents
          const contents = await listProjectFolderContents(project);
          setItems(contents);
          setInitialized(true);
        } catch (err) {
          console.error('Error initializing existing folder:', err);
          let errorMessage = 'Failed to initialize folder';
          
          if (err instanceof Error) {
            if (err.message.includes('consent_required') || err.message.includes('invalid_grant')) {
              errorMessage = 'Authentication required. Please sign in again.';
            } else if (err.message.includes('not found') || err.message.includes('access denied')) {
              errorMessage = 'Folder not found or access denied. Please try linking the folder again.';
            } else {
              errorMessage = err.message;
            }
          }
          
          setError(errorMessage);
          // Reset state if folder can't be accessed
          setInitialized(false);
          setFolderUrl(null);
          setItems([]);
        } finally {
          setLoading(false);
        }
      }
    };

    initializeExistingFolder();
  }, [project.onedrive_folder_id, initialized]);

  // Add a separate effect to handle authentication state
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const isAuthenticated = await isSignedIn();
        if (!isAuthenticated && project.onedrive_folder_id) {
          setError('Please sign in to access project folders');
          setInitialized(false);
          setFolderUrl(null);
          setItems([]);
        }
      } catch (err) {
        console.error('Error checking authentication:', err);
        setError('Failed to check authentication status');
      }
    };

    checkAuth();
  }, [project.onedrive_folder_id]);

  const handleFileUpload = async (files: FileList) => {
    try {
      setUploading(true);
      setError(null);
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        await uploadFileToProjectFolder(project, file);
      }
      
      // Refresh the folder contents after upload
      await fetchFolderContents();
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    if (e.dataTransfer.files.length > 0) {
      await handleFileUpload(e.dataTransfer.files);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleLinkFolder = async (folder: DriveItem) => {
    try {
      setLinkingFolder(true);
      setError(null);
      
      // Show waiting message
      setError('Please wait while we link your folder...');
      
      // Link the folder
      await linkExistingFolderToProject(project, folder.id);
      
      // Force a page refresh to ensure clean state
      window.location.reload();
    } catch (err) {
      console.error('Error linking folder:', err);
      setError(err instanceof Error ? err.message : 'Failed to link folder');
    } finally {
      setLinkingFolder(false);
    }
  };

  const handleUnlinkFolder = async () => {
    try {
      setUnlinkingFolder(true);
      setError(null);
      
      await unlinkFolderFromProject(project);
      setFolderUrl(null);
      setInitialized(false);
      setItems([]);
      setUnlinkDialogOpen(false);
      
      // Force a page refresh to ensure clean state
      window.location.reload();
    } catch (err) {
      console.error('Error unlinking folder:', err);
      setError(err instanceof Error ? err.message : 'Failed to unlink folder');
    } finally {
      setUnlinkingFolder(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <WorkspaceCardHeader
          icon={MdFolderOpen}
          title="Project Files"
          description="Files are stored in your connected OneDrive folder."
          action={(
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              {initialized && (
                <Chip
                  size="sm"
                  variant="plain"
                  startDecorator={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.500' }} />}
                >
                  Connected to OneDrive
                </Chip>
              )}
              {folderUrl && (
                <Button
                  size="sm"
                  variant="plain"
                  component="a"
                  href={folderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  startDecorator={<IconWrapper icon={FaExternalLinkAlt} />}
                >
                  Open in OneDrive
                </Button>
              )}
              {initialized && (
                <Button
                  variant="plain"
                  color="danger"
                  size="sm"
                  startDecorator={<IconWrapper icon={FaUnlink} />}
                  onClick={() => setUnlinkDialogOpen(true)}
                  disabled={unlinkingFolder}
                >
                  Unlink
                </Button>
              )}
            </Stack>
          )}
        />

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!initialized ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'stretch' }}>
            <Box
              sx={{
                flex: 1,
                border: '1px dashed',
                borderColor: 'neutral.300',
                borderRadius: 'md',
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography level="body-sm" fontWeight="lg">No project folder yet</Typography>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  Create a OneDrive folder or link an existing one.
                </Typography>
              </Box>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button
                  variant="solid"
                  color="primary"
                  size="sm"
                  startDecorator={loading ? <CircularProgress size="sm" /> : <IconWrapper icon={FaFolder} />}
                  onClick={initializeFolder}
                  disabled={loading || !project.id}
                >
                  {loading ? 'Creating...' : 'Create Project Folder'}
                </Button>
                <Button
                  variant="outlined"
                  color="neutral"
                  size="sm"
                  startDecorator={<IconWrapper icon={FaLink} />}
                  onClick={() => setLinkDialogOpen(true)}
                  disabled={loading || !project.id}
                >
                  Link Existing Folder
                </Button>
              </Stack>
            </Box>
          </Stack>
        ) : (
          <>
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems="stretch">
              <Box
                component="label"
                sx={{
                  flex: 1,
                  border: '1px dashed',
                  borderColor: dragOver ? 'primary.400' : 'neutral.300',
                  borderRadius: 'md',
                  px: 1.5,
                  py: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  cursor: uploading ? 'wait' : 'pointer',
                  bgcolor: dragOver ? 'primary.50' : 'transparent',
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Stack direction="row" alignItems="center" gap={1.25}>
                  <Box sx={{ color: 'primary.500', display: 'flex' }}>
                    <IconWrapper icon={FaUpload} />
                  </Box>
                  <Box>
                    <Typography level="body-sm" fontWeight="lg">Upload files</Typography>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      Drag and drop files here, or click to browse
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  size="sm"
                  variant="outlined"
                  startDecorator={<IconWrapper icon={FaUpload} />}
                  loading={uploading}
                  component="span"
                >
                  Upload files
                </Button>
                <input
                  type="file"
                  multiple
                  hidden
                  disabled={uploading}
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                />
              </Box>
              <Box
                onClick={() => setFilesOpen((open) => !open)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setFilesOpen((open) => !open);
                  }
                }}
                sx={{
                  minWidth: { md: 220 },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 'md',
                  px: 1.5,
                  py: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  cursor: 'pointer',
                }}
              >
                <Stack direction="row" alignItems="center" gap={1.25}>
                  <Box sx={{ color: 'primary.500', display: 'flex' }}>
                    <IconWrapper icon={FaFile} />
                  </Box>
                  <Box>
                    <Typography level="body-sm" fontWeight="lg">
                      {loading ? 'Loading…' : `${fileCount} ${fileCount === 1 ? 'file' : 'files'}`}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Project documents</Typography>
                  </Box>
                </Stack>
                <Box sx={{ color: 'neutral.400', display: 'flex', transform: filesOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }}>
                  <IconWrapper icon={FaChevronRight} />
                </Box>
              </Box>
            </Stack>
            {filesOpen && (
              <Box sx={{ mt: 1.5 }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size="sm" />
                  </Box>
                ) : items.length === 0 ? (
                  <Typography level="body-sm" sx={{ color: 'neutral.500', px: 0.5 }}>
                    No files in this folder
                  </Typography>
                ) : (
                  <List>
                    {items.map((item) => (
                      <React.Fragment key={item.id}>
                        <ListItem
                          sx={{
                            '&:hover': {
                              bgcolor: 'background.level1',
                            },
                          }}
                        >
                          <ListItemContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconWrapper
                                icon={item.folder ? FaFolder : FaFile}
                                color={item.folder ? 'warning.500' : 'primary.500'}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Typography level="body-md">{item.name}</Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                  <Chip size="sm" variant="soft">
                                    {item.folder ? 'Folder' : formatFileSize(item.size || 0)}
                                  </Chip>
                                  <Chip size="sm" variant="soft">
                                    {formatDate(item.lastModifiedDateTime)}
                                  </Chip>
                                </Stack>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  component="a"
                                  href={item.webUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <IconWrapper icon={FaDownload} />
                                </IconButton>
                              </Box>
                            </Box>
                          </ListItemContent>
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </>
        )}

        {/* Replace Link Folder Dialog with Folder Browser */}
        <Modal open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)}>
          <ModalDialog sx={{ maxWidth: 600, width: '100%' }}>
            <Typography level="h4" mb={2}>Link Existing Folder</Typography>
            <Typography mb={2}>
              Browse your OneDrive folders to select a folder to link to this project.
            </Typography>
            <FolderBrowser
              onSelectFolder={handleLinkFolder}
              onCancel={() => setLinkDialogOpen(false)}
            />
          </ModalDialog>
        </Modal>

        {/* Add Unlink Confirmation Dialog */}
        <Modal open={unlinkDialogOpen} onClose={() => setUnlinkDialogOpen(false)}>
          <ModalDialog
            variant="outlined"
            role="alertdialog"
            aria-labelledby="unlink-dialog-title"
            aria-describedby="unlink-dialog-description"
          >
            <Typography id="unlink-dialog-title" level="title-lg" mb={2}>
              Unlink Project Folder
            </Typography>
            <Typography id="unlink-dialog-description" mb={3}>
              Are you sure you want to unlink this folder from the project? This will remove the connection between the project and the folder, but the folder and its contents will remain in OneDrive.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setUnlinkDialogOpen(false)}
                disabled={unlinkingFolder}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="danger"
                onClick={handleUnlinkFolder}
                disabled={unlinkingFolder}
                startDecorator={unlinkingFolder ? <CircularProgress size="sm" /> : <IconWrapper icon={FaUnlink} />}
              >
                {unlinkingFolder ? 'Unlinking...' : 'Unlink Folder'}
              </Button>
            </Box>
          </ModalDialog>
        </Modal>
      </CardContent>
    </Card>
  );
};

export default ProjectFolderViewer; 